"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { fetchOrganizationDepartment } from "../../../Apis/Department-api";
import { fetchOrganizationLevels } from "../../../Apis/OrganizationLevel-api";
import {
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DESIGNATION_TOOLBAR_FILTERS = [
  { urlKey: "department_id", field: "organization_department_id" },
  { urlKey: "level_id", field: "organization_level_id" },
];

const DEFAULT_COLUMNS = [
  {
    field: "designation_name",
    label: "Designation",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation_short_name",
    label: "Designation Short Name",
    visible: true,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_name",
    label: "Department Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "level_name",
    label: "Level",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_designation_id",
  sortOrder: "desc",
};

const resolveDepartmentName = (item) => {
  if (typeof item?.department === "object" && item.department !== null) {
    return item.department.department_name || "";
  }
  if (typeof item?.department === "string") {
    return item.department;
  }
  return item?.department_name || item?.departmentName || "";
};

const resolveLevelName = (item) =>
  item?.level_name ||
  item?.levelName ||
  item?.level?.level_name ||
  "";

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "designation_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

function OrganizationDesignationsList() {
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [departments, setDepartments] = useState([]);
  const [levels, setLevels] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    Promise.all([
      fetchOrganizationDepartment(org.organization_id),
      fetchOrganizationLevels(org.organization_id),
    ])
      .then(([deptData, levelData]) => {
        if (cancelled) return;
        setDepartments(asOptionList(deptData, ["Departments", "data"]));
        setLevels(asOptionList(levelData, ["Levels", "data"]));
      })
      .catch((error) => {
        console.error("Failed to load designation toolbar filters", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          appliedFilters !== null
            ? normalizeAppliedFilters(appliedFilters)
            : buildFiltersFromParams(searchParams),
          searchParams,
          DESIGNATION_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_designation_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/designations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          return;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const mappedData = data.map((item) => {
          const deptName = resolveDepartmentName(item);
          const levelName = resolveLevelName(item);

          return {
            ...item,
            organization_designation_id: item.organization_designation_id,
            designation_name: item.designation_name || "",
            designation_short_name: item.designation_short_name || "",
            id: item.organization_designation_id,
            department_name: deptName,
            departmentName: deptName,
            department: deptName,
            level_name: levelName,
            levelName: levelName,
            organization_level_id: item.organization_level_id || null,
            cardname: (deptName || "-").toUpperCase(),
            carddescription: item.designation_name || "",
          };
        });

        setDesignations(mappedData);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching designations:", error);
        toast.error("Failed to load designations");
        setDesignations([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams, appliedFilters]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Designations_grid_${org.organization_id}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  const deletedesignation = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await axios.delete(
          `${MAIN_URL}/api/organizations/${org_id}/designation/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            validateStatus: function (status) {
              return status >= 200 && status < 500;
            },
          }
        );

        if (response.status === 200) {
          toast.success(response.data.message);
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            response.data.message ||
            response.data.errors?.[Object.keys(response.data.errors)[0]]?.[0] ||
            "Failed to delete Designation";

          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        }
        console.error("Delete failed:", error);
        toast.error("Something went wrong. Please try again later.");
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/designation/edit/${item.id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`/organization/designation/view/${item.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback((nextFilters) => {
    // Use filters from the table directly — do NOT clone URLSearchParams here.
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Designations"}
        btnName={"Add Designation"}
        Data={designations}
        add_action={"DESIGNATION_ADD"}
        delete_action={"DESIGNATION_DELETE"}
        Icons={[
          <LocalPoliceIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <LocalPoliceIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "department's designation",
          "department designations",
          "Add Department's designations",
          "department designation, Deleting this designation will also remove all related employees",
        ]}
        Route={"/organization/designation"}
        setData={setDesignations}
        DeleteFunc={deletedesignation}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_designations"
          primaryKey="organization_designation_id"
          heading="Designations"
          data={designations}
          sortname={"organization_designation_id"}
          showActions={true}
          Route="/organization/designation"
          filterModule="organization_designations"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Department"
                urlKey="department_id"
                allLabel="All departments"
                options={departments}
                valueKey="organization_department_id"
                labelKey="department_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Level"
                urlKey="level_id"
                allLabel="All levels"
                options={levels}
                valueKey="organization_level_id"
                labelKey="level_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          DeleteFunc={deletedesignation}
          EditFunc={handleEdit}
          edit_delete_action={["DESIGNATION_DELETE", "DESIGNATION_EDIT"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationDesignationsList;
